import { EntityType, useAppSelector } from '@frontend/common';
import {
    CheckboxInput,
    ColorInput,
    CountryInput,
    DateInput,
    DimensionInput,
    EmailInput,
    LocationInput,
    NumberInput,
    PasswordInput,
    PhoneInput,
    TextArea,
    TextInput
} from '@frontend/components/basic-forms';
import { AsyncComponent } from '@frontend/components/common';
import { EnumSelectListV2, ObjectSelectMapper } from '@frontend/components/select';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { formStore, updateField } from '@frontend/repository';
import { CustomRoleTemplateSelect } from '@frontend/role/select';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { useSelector } from 'react-redux';

const ID = 'form-field';
const translationKeys = { nl_be: 'NL', en_us: 'EN', fr_fr: 'FR', de_de: 'DE' };
const commonKeys = ['location', 'absolute', 'dimension', 'permissions', 'inner', 'outer', 'country', 'email', 'phone', 'phone_number', 'mobile'];
const fallback = (
    <span className='placeholder-glow'>
        <span className='placeholder'>loading</span>
    </span>
);

function isUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
}

interface Props<S extends object> {
    dispatch: ThunkDispatch<any, any, Action>;
    formId: string;
    fieldKey: keyof S;
    label?: React.ReactNode;
    objectSelectKeyOverwrite?: string;
    fieldOverwrite?: React.ReactNode;
    enumOverwrite?: string;
    expectedType?: 'object' | 'boolean' | 'number' | 'string' | 'entity' | 'textarea';
    fieldProps?: any;

    required?: boolean;
    hidden?: boolean;

    className?: string;
    id?: string;
}

export const FormField = <S extends object>(props: Props<S>) => {
    const store = useAppSelector(useSelector, formStore);
    const form = store[props.formId];
    if (!form) return null;
    if (props.hidden && !props.required) return null;

    const value = (form.value as S)[props.fieldKey];
    let finalKey = props.label;
    if (finalKey === undefined) {
        const splitKey = (props.fieldKey as string).split('_');
        if (splitKey.length > 1 && splitKey[splitKey.length - 1] === 'id') {
            splitKey.pop();
            const joinedKey = splitKey.join('_').toUpperCase();
            finalKey = (CommonMessage.OBJECTS[joinedKey as string as keyof typeof CommonMessage.OBJECTS] as any)
                ? ((CommonMessage.OBJECTS[joinedKey as string as keyof typeof CommonMessage.OBJECTS] as any).DEFAULT ?? undefined)
                : undefined;
        } else if (Object.keys(CommonMessage.FORMS).includes((props.fieldKey as string).toUpperCase())) {
            finalKey =
                typeof CommonMessage.FORMS[(props.fieldKey as string).toUpperCase() as keyof typeof CommonMessage.FORMS] === 'function'
                    ? (CommonMessage.FORMS[(props.fieldKey as string).toUpperCase() as keyof typeof CommonMessage.FORMS] as any)()
                    : (CommonMessage.FORMS[(props.fieldKey as string).toUpperCase() as keyof typeof CommonMessage.FORMS] as any);
        } else if (Object.keys(translationKeys).includes((props.fieldKey as string).slice(-5))) {
            // slice -5 because translation fields will always end with their locale
            const found = Object.entries(translationKeys).find((k) => (props.fieldKey as string).includes(k[0]));
            finalKey = (props.fieldKey as string)[0].toUpperCase() + (props.fieldKey as string).slice(1).replace(found![0], `(${found![1]})`);
            finalKey = (finalKey as string).split('_').join(' ');
        } else {
            finalKey = (props.fieldKey as string)[0].toUpperCase() + (props.fieldKey as string).slice(1);
            finalKey = (finalKey as string)
                .split('_')
                .filter((part) => part !== 'id')
                .join(' ')
                .replace('timestamp', '');
        }
    }

    const fieldKey = `${props.formId}-${props.fieldKey as string}`;
    const fieldProps = {
        className: props.className,
        id: ID + '-' + (props.fieldKey as string),
        label: finalKey,
        value,
        onChange: (value: any) => props.dispatch(updateField({ formId: props.formId, key: props.fieldKey as string, value })),
        onSelect: (value: any) => props.dispatch(updateField({ formId: props.formId, key: props.fieldKey as string, value: value?.value ?? value })),
        required: props.required,
        submitted: false,
        allowURLChange: false,
        ...props.fieldProps
    };

    if (props.fieldOverwrite) return props.fieldOverwrite;

    if (
        (props.enumOverwrite && EnumSelectListV2[props.enumOverwrite as keyof typeof EnumSelectListV2]) ||
        EnumSelectListV2[props.fieldKey as keyof typeof EnumSelectListV2]
    ) {
        const EnumInput = props.enumOverwrite
            ? EnumSelectListV2[props.enumOverwrite as keyof typeof EnumSelectListV2]
            : EnumSelectListV2[props.fieldKey as keyof typeof EnumSelectListV2];
        return (
            <AsyncComponent fallback={fallback}>
                <EnumInput
                    key={fieldKey}
                    {...fieldProps}
                    fieldKey={props.fieldKey as string}
                />
            </AsyncComponent>
        );
    }

    if (commonKeys.includes(props.fieldKey as string)) {
        switch (props.fieldKey as string) {
            case 'location':
            case 'absolute':
                return (
                    <LocationInput
                        key={fieldKey}
                        {...fieldProps}
                    />
                );
            case 'dimension':
            case 'inner':
            case 'outer':
                return (
                    <DimensionInput
                        key={fieldKey}
                        {...fieldProps}
                    />
                );
            case 'permissions':
                return (
                    <CustomRoleTemplateSelect
                        key={fieldKey}
                        {...fieldProps}
                        entity_type={(form.value as S)['entity_type' as keyof S] as EntityType | undefined}
                    />
                );
            case 'country':
                return (
                    <CountryInput
                        key={fieldKey}
                        {...fieldProps}
                    />
                );
            case 'email':
                return (
                    <EmailInput
                        key={fieldKey}
                        {...fieldProps}
                    />
                );
            case 'phone':
            case 'phone_number':
            case 'mobile':
                return (
                    <PhoneInput
                        key={fieldKey}
                        {...fieldProps}
                    />
                );
        }
    }

    if (props.fieldKey === 'secret') {
        return (
            <PasswordInput
                key={fieldKey}
                {...fieldProps}
            />
        );
    }

    if ((props.fieldKey as string).includes('time') || (props.fieldKey as string).includes('date')) {
        return (
            <AsyncComponent fallback={fallback}>
                <DateInput
                    key={fieldKey}
                    {...fieldProps}
                    value={value ? new Date(value as string) : undefined}
                    onChange={(value) => props.dispatch(updateField({ formId: props.formId, key: props.fieldKey as string, value: value?.toISOString() }))}
                    dateTime={fieldProps?.dateTime ?? (props.fieldKey as string).includes('time')}
                />
            </AsyncComponent>
        );
    }

    if (
        (props.fieldKey as string).includes('color') ||
        [
            'brand_primary',
            'brand_secondary',
            'brand_muted',
            'brand_light',
            'brand_disabled',
            'status_danger',
            'status_success',
            'status_warning',
            'status_info',
            'foreground_default',
            'background_surface',
            'background_default',
            'border_default',
            'always_white',
            'always_black'
        ].includes(props.fieldKey as string)
    ) {
        return (
            <ColorInput
                key={fieldKey}
                {...fieldProps}
            />
        );
    }

    if (typeof value === 'number' || props.expectedType === 'number')
        return (
            <NumberInput
                key={fieldKey}
                {...fieldProps}
            />
        );

    if (typeof value === 'boolean' || props.expectedType === 'boolean')
        return (
            <CheckboxInput
                key={fieldKey}
                {...fieldProps}
            />
        );

    if (
        (typeof value === 'string' && isUUID(value)) ||
        (((props.fieldKey as string) === 'id' || (props.fieldKey as string).endsWith('_id')) && !(props.fieldKey as string).includes('identifier')) ||
        props.expectedType === 'entity'
    ) {
        return (
            <ObjectSelectMapper
                key={fieldKey}
                {...fieldProps}
                objectKey={props.objectSelectKeyOverwrite ?? props.fieldKey}
                allowURLChange={false}
            />
        );
    }

    if (Array.isArray(value)) {
        return (
            <TextInput
                key={fieldKey}
                {...fieldProps}
                value={(value as []).join(', ')}
                onChange={(value) =>
                    props.dispatch(updateField({ formId: props.formId, key: props.fieldKey as string, value: value.split(',').map((item) => item.trim()) }))
                }
            />
        );
    }

    if (props.expectedType === 'textarea')
        return (
            <TextArea
                key={fieldKey}
                {...fieldProps}
                type='textarea'
            />
        );

    if (typeof value === 'string' || props.expectedType === 'string')
        return (
            <TextInput
                key={fieldKey}
                {...fieldProps}
            />
        );
    if (value === undefined || value === null)
        return (
            <TextInput
                key={fieldKey}
                {...fieldProps}
            />
        );

    Logger.warn('Unknown type', value, props.fieldKey);
    return <></>;
};
